import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../context/UserContext';
import { GenericHeader } from '../../StyledComponents/Modal/generic';
import { Label } from "../../StyledComponents/Input/generic";
import Loading from '../Loading/Loading';
import Clients, { ClientType } from "../../service/Clients";
import { ToastContainer, toast } from "react-toastify";
import AttToken from '../../helpers/attToken';
import queryClient from '../../service/query';
import { userTag } from '../Users/TagControll';
import { Autocomplete, TextField } from '@mui/material';
import { useMask } from '@react-input/mask';
import { trackEventMatomo } from '../../helpers/matomo';

export type TagType = {
  id: string,
  name: string,
}
const customerService = new Clients();

export default function ChangeCustomerModal({ selectedCustomer, customers, customerTags }: { selectedCustomer: string, customers: ClientType[], customerTags: userTag[] }) {
  const { openModal, popUp, escape } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [alteredCustomer, setAlteredCustomer] = useState<ClientType>({
    id: '',
    contato: '',
    dataCadastro: 0,
    empresa: ' ',
    foto: ' ',
    listaGerencia: [],
    nome: '',
    responsavel: ' ',
    status: '',
    locais: [],
    tags: [],
    tiposServico: []
  })

  const inputRef = useMask({ mask: '(__) ____-_____', replacement: '_' });

  const { t } = useTranslation('translation');

  const updateCustomer = useCallback(async () => {
    try {
      toast.warn(`${t('toast.savingChanges')}`)
      const token = await AttToken();
      if (token) {
        await customerService.updateClient({ ...token }, alteredCustomer);
        queryClient.resetQueries(['GET_CUSTOMERS'])
        openModal();
        toast.success(t<string>(`customerPage.toastChangeCustomer`));
      }
    }
    catch (err) {
      return console.log(err);
    }
  }, [alteredCustomer, openModal, customerService, t])

  useEffect(() => {
    if (loading && customers.length > 0) {
      const { nome, contato, status, id, tags, dataCadastro, empresa, tiposServico, locais, listaGerencia } = customers.filter(e => e.id === selectedCustomer)[0];
      setAlteredCustomer({
        ...alteredCustomer,
        id,
        nome,
        contato,
        status,
        tags: tags ? tags as string[] : [] as string[],
        empresa,
        dataCadastro,
        tiposServico,
        locais,
        listaGerencia
      })
      setLoading(false);
    }
  }, [loading, selectedCustomer, customers, alteredCustomer]);

  return loading ? (<Loading />) : (
    <div className={`modal ${popUp ? "modal is-active" : "modal"}`}>
      <ToastContainer />
      <div className="modal-background" onKeyDown={() => {
        escape()
        trackEventMatomo('modal edição cliente', 'click', 'modal', 'fechar')
      }} onClick={() => {
        openModal()
        trackEventMatomo('modal edição cliente', 'click', 'modal', 'fechar')
      }} />
      <div className="modal-card" onSubmit={(e: React.FormEvent) => e.preventDefault()}>
        <header className="modal-card-head" style={{ display: "flex", flexDirection: "column" }}>
          <GenericHeader>
            {t('customerPage.AlterarCliente')}
          </GenericHeader>
        </header>
        <div className="modal-card-body">
          <div
            style={{
              display: "flex",
              flexDirection: "column"
            }}
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              onClick={() => trackEventMatomo('modal edição cliente', 'click', 'input', 'editar valor')}
              style={{ marginBottom: '20px' }}
              label={t('name')}
              fullWidth
              inputProps={{ maxLength: 100 }}
              type="text"
              value={alteredCustomer.nome}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAlteredCustomer({ ...alteredCustomer, nome: e.target.value })}
            />
            <TextField
              onClick={() => trackEventMatomo('modal edição cliente', 'click', 'input', 'editar valor')}
              style={{ marginBottom: '10px' }}
              label={t('name')}
              fullWidth
              type='tel'
              inputProps={{ maxLength: 100 }}
              inputRef={inputRef}
              value={alteredCustomer.contato}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAlteredCustomer({ ...alteredCustomer, contato: e.target.value })}
            />
            <Autocomplete
              onClick={() => trackEventMatomo('modal edição cliente', 'click', 'select', 'seleciona valor')}
              style={{ marginBottom: '10px' }}
              multiple
              value={alteredCustomer.tags?.map((ele) => (
                {
                  value: ele,
                  label: customerTags.find((elem) => elem.id === ele)?.name
                }
              )) || undefined}
              id="combo-box-demo"
              sx={{ marginTop: '10px' }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              filterSelectedOptions
              noOptionsText={`${t('checklists.Modal.noAvailableTags')}`}
              options={
                customerTags
                  ?.sort((a, b) => { return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : - 1 })
                  ?.map((ele: any) => ({
                    label: ele.name,
                    value: ele.id,
                  }))}
              onChange={(event: any, newValue: { label: string, value: string }[] | null, reason: any, details: any) => {
                const value = details?.option.value;
                if (alteredCustomer.tags?.includes(value)) {
                  setAlteredCustomer({ ...alteredCustomer, tags: alteredCustomer.tags ? alteredCustomer.tags.filter((el) => el !== value) : [] })
                } else {
                  setAlteredCustomer({ ...alteredCustomer, tags: alteredCustomer.tags ? [...alteredCustomer.tags, value] : [value] })
                }
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <span>{option.label}</span>
                </li>)}
              renderInput={(params) => <TextField {...params} label={t('tag')} />}
            />
            <Label>{t('status')}</Label>
            <select
              onClick={() => trackEventMatomo('modal edição cliente', 'click', 'select', 'seleciona valor')}
              className='select is-small'
              onChange={(e) => setAlteredCustomer({ ...alteredCustomer, status: e.target.value })}
              defaultValue={alteredCustomer.status}
            >
              <option value={'ativo'}>{t('active')}</option>
              <option value={'inativo'}>{t('inactive')}</option>
            </select>
          </div>
        </div>
        <div className='modal-card-foot' style={{ display: "flex", justifyContent: "space-between" }}>
          <button
            type='button'
            className="button is-success is-small"
            onClick={() => {
              trackEventMatomo('modal edição cliente', 'click', 'button', 'salvar')
              updateCustomer()
              openModal()
            }}
          >
            {t('save')}
          </button>
          <button
            type="button"
            onClick={() => {
              trackEventMatomo('modal edição cliente', 'click', 'button', 'cancelar')
              openModal()
            }}
            className="button is-small is-warning"
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}